import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";

/** simple debounce */
function useDebounce(value, delay = 500) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** money helpers */
const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};
const money2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "0.00");

export default function BookingPaymentsList() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // filters
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [bookingId, setBookingId] = useState("");
  const [userId, setUserId] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [refundOpen, setRefundOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [refundAmountTouched, setRefundAmountTouched] = useState(false);

  const [refundForm, setRefundForm] = useState({
    booking_id: "",
    payment_id: "",
    bookingPayment_id: "",
    user_id: "",
    totalfare: "0.00",
    convenience_fee: "0",
    applied_promo_code: 0,
    applied_promo_code_amount: "0",
    applied_cashback: 0,
    cashback_points: "0",
    is_wallet_applied: 0,
    wallet_points: "0",
    refund_type: "NORMAL_REFUND", 
    instant_refund_reason: "",
    cancellation_amount: "",
    refund_amount: "",
    full_payment: false,
    cancel_type: "Change Itinerary / Reissue",
    cancel_insurance: false,
    remarks: "",
    attachment: null,
  });

  const fetchBookingPayments = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;

    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/booking-payments", {
        params: {
          search: debouncedSearch || undefined,
          booking_id: bookingId || undefined,
          user_id: userId || undefined,
          page: nextPage,
          limit: nextLimit,
          sortBy: "created_at",
          order: "DESC",
        },
      });

      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load booking payments";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingPayments({ page: 1 });
  }, []);

  useEffect(() => {
    setPage(1);
    fetchBookingPayments({ page: 1 });
  }, [debouncedSearch]);

  useEffect(() => {
    setPage(1);
    fetchBookingPayments({ page: 1 });
  }, [bookingId, userId]);

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchBookingPayments({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchBookingPayments({ page: p });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 20;
    setLimit(v);
    setPage(1);
    fetchBookingPayments({ page: 1, limit: v });
  };

  const clearFilters = () => {
    setSearch("");
    setBookingId("");
    setUserId("");
    setPage(1);
  };

  const setField = (key, value) => {
    setRefundForm((p) => ({ ...p, [key]: value }));
  };

  const totalFareNum = useMemo(() => toNum(refundForm.totalfare), [refundForm.totalfare]);
  const convenienceFeeNum = useMemo(() => toNum(refundForm.convenience_fee), [refundForm.convenience_fee]);

  const promoApplied = useMemo(() => Number(refundForm.applied_promo_code) === 1, [refundForm.applied_promo_code]);
  const promoAmountNum = useMemo(
    () => (promoApplied ? toNum(refundForm.applied_promo_code_amount) : 0),
    [promoApplied, refundForm.applied_promo_code_amount]
  );

  const walletApplied = useMemo(
    () => Number(refundForm.is_wallet_applied) === 1,
    [refundForm.is_wallet_applied]
  );

  const walletPointsNum = useMemo(
    () => (walletApplied ? toNum(refundForm.wallet_points) : 0),
    [walletApplied, refundForm.wallet_points]
  );

  const maxRefundable = useMemo(() => {
    const v = totalFareNum - convenienceFeeNum - promoAmountNum - walletPointsNum;
    return Math.max(0, Number.isFinite(v) ? v : 0);
  }, [totalFareNum, convenienceFeeNum, promoAmountNum, walletPointsNum]);

  const cancellationAmountNum = useMemo(() => toNum(refundForm.cancellation_amount), [refundForm.cancellation_amount]);

  const suggestedRefundAfterCancellation = useMemo(() => {
    const v = maxRefundable - cancellationAmountNum;
    return Math.max(0, Number.isFinite(v) ? v : 0);
  }, [maxRefundable, cancellationAmountNum]);

  const refundAmountNum = useMemo(() => toNum(refundForm.refund_amount), [refundForm.refund_amount]);

  const refundAmountError = useMemo(() => {
    if (!refundOpen) return "";
    if (!refundForm.refund_amount && refundForm.refund_amount !== "0") return "Refund amount is required";
    if (refundAmountNum < 0) return "Refund amount cannot be negative";
    if (refundAmountNum > maxRefundable) return "Invalid refund amount (exceeds max refundable)";
    if (refundAmountNum > totalFareNum) return "Invalid refund amount (exceeds total fare)";
    return "";
  }, [refundOpen, refundForm.refund_amount, refundAmountNum, maxRefundable, totalFareNum]);

  /** One row per trace_id (same trace shows once; primary = latest by created_at). Refund disabled if any payment in trace is refunded. */
  const displayRows = useMemo(() => {
    const byTrace = new Map();
    rows.forEach((r) => {
      const key = r.trace_id != null && r.trace_id !== "" ? r.trace_id : r.bookingPayment_id;
      const existing = byTrace.get(key);
      if (!existing || new Date(r.created_at) > new Date(existing.created_at)) byTrace.set(key, r);
    });
    return Array.from(byTrace.values());
  }, [rows]);

  useEffect(() => {
    if (!refundOpen) return;

    if (refundForm.full_payment) {
      setRefundForm((p) => ({ ...p, refund_amount: money2(maxRefundable) }));
      return;
    }

    if (!refundAmountTouched) {
      setRefundForm((p) => ({ ...p, refund_amount: money2(suggestedRefundAfterCancellation) }));
    }
  }, [refundOpen, refundForm.full_payment, refundAmountTouched, maxRefundable, suggestedRefundAfterCancellation]);

  const openRefundModal = (row) => {
    setSelected(row);
    setRefundAmountTouched(false);

    setRefundForm({
      booking_id: row?.booking_id || "",
      payment_id: row?.payment_id || "",
      bookingPayment_id: row?.bookingPayment_id || "",
      user_id: row?.user_id || "",
      totalfare: row?.totalfare ?? "0.00",
      convenience_fee: row?.convenience_fee ?? "0",
      applied_promo_code: Number(row?.applied_promo_code || 0),
      applied_promo_code_amount: row?.applied_promo_code_amount ?? "0",
      applied_cashback: Number(row?.applied_cashback || 0),
      cashback_points: row?.cashback_points ?? "0",
      is_wallet_applied: Number(row?.is_wallet_applied || 0),
      wallet_points: row?.wallet_points ?? "0",
      refund_type: "NORMAL_REFUND",
      instant_refund_reason: "",
      cancellation_amount: "",
      refund_amount: "", 
      full_payment: false,
      cancel_type: "Change Itinerary / Reissue",
      cancel_insurance: false,
      remarks: "",
      attachment: null,
    });

    setRefundOpen(true);
  };

  const closeRefundModal = () => {
    setRefundOpen(false);
    setSelected(null);
    setSubmitting(false);
    setRefundAmountTouched(false);
  };

  const openViewRefundModal = async (row) => {
    try {
      setViewLoading(true);
      const resp = await api.get(`/admin/booking-payments/${row.bookingPayment_id}/refund`);
      setViewRow(resp.data?.data || null);
      setViewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load refund");
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewRefundModal = () => {
    setViewOpen(false);
    setViewRow(null);
  };

  const submitRefund = async (e) => {
    e.preventDefault();

    if (refundAmountError) {
      toast.error(refundAmountError);
      return;
    }

    if (refundForm.refund_type === "INSTANT_REFUND" && !refundForm.instant_refund_reason?.trim()) {
      toast.error("Instant refund reason is required");
      return;
    }

    if (!refundForm.remarks?.trim()) {
      toast.error("Remarks is required");
      return;
    }

    const bookingPaymentId = refundForm.bookingPayment_id || selected?.bookingPayment_id;
    if (!bookingPaymentId) {
      toast.error("bookingPaymentId missing");
      return;
    }

    try {
      setSubmitting(true);

      const fd = new FormData();
      fd.append("booking_id", refundForm.booking_id);
      fd.append("booking_ticket_number", refundForm.booking_ticket_number);
      fd.append("payment_id", refundForm.payment_id);
      fd.append("user_id", refundForm.user_id);
      fd.append("cancel_type", refundForm.cancel_type);
      fd.append("remarks", refundForm.remarks);

      fd.append("cancel_insurance", refundForm.cancel_insurance ? "1" : "0");
      fd.append("full_payment", refundForm.full_payment ? "1" : "0");

      fd.append("totalfare", String(refundForm.totalfare ?? ""));
      fd.append("convenience_fee", String(refundForm.convenience_fee ?? ""));
      fd.append("applied_promo_code", String(refundForm.applied_promo_code ?? 0));
      fd.append("applied_promo_code_amount", String(refundForm.applied_promo_code_amount ?? ""));
      fd.append("applied_cashback", String(refundForm.applied_cashback ?? 0));
      fd.append("cashback_points", String(refundForm.cashback_points ?? ""));

      fd.append("is_wallet_applied", String(refundForm.is_wallet_applied ?? 0));
      fd.append("wallet_points", String(refundForm.wallet_points ?? ""));

      fd.append("refund_type", String(refundForm.refund_type ?? "NORMAL_REFUND"));
      fd.append("instant_refund_reason", String(refundForm.instant_refund_reason ?? ""));

      fd.append("cancellation_amount", String(refundForm.cancellation_amount ?? ""));
      fd.append("refund_amount", String(refundForm.refund_amount ?? ""));

      if (refundForm.attachment) fd.append("attachment", refundForm.attachment);

      await api.post(`/admin/booking-payments/${bookingPaymentId}/refund`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Refund request submitted");

      closeRefundModal();
      await fetchBookingPayments();
    } catch (e2) {
      toast.error(e2?.response?.data?.message || e2.message || "Refund submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Booking Payments</h1>
          {/* <p className="text-sm text-gray-600 mt-1">Search filters instantly (no Apply needed)</p> */}
        </div>

        <button
          type="button"
          onClick={clearFilters}
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start sm:self-auto"
        >
          Clear Filters
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Search by booking_id / payment_id / user_id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[260px]">
          <label className="text-sm font-semibold">Booking ID</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Filter by booking_id"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[260px]">
          <label className="text-sm font-semibold">User ID</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Filter by user_id"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Booking Payment List</span>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>

            <button
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : displayRows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No booking payments found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                {/* <th className="px-4 py-3">Booking ID</th> */}
                <th className="px-4 py-3">Booking Ticket Number</th>
                <th className="px-4 py-3">Payment ID</th>
                <th className="px-4 py-3">User Name</th>
                <th className="px-4 py-3">User Email</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="border-t">
              {displayRows.map((r) => {
                const isRefunded = !!(r.has_refund || r.trace_has_refund);
                return (
                  <tr key={r.trace_id ? `trace-${r.trace_id}` : r.bookingPayment_id} className="border-t hover:bg-gray-50">
                    {/* <td className="px-4 py-3 font-semibold">{r.booking_id}</td> */}
                    <td className="px-4 py-3 font-semibold">{r.booking_ticket_number}</td>
                    <td className="px-4 py-3">{r.payment_id}</td>
                    <td className="px-4 py-3">{r.user_name}</td>
                    <td className="px-4 py-3">{r.user_email}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50"
                          disabled={isRefunded}
                          onClick={() => openRefundModal(r)}
                          title={isRefunded ? "Refund already submitted for this trace" : "Create refund"}
                        >
                          Refund
                        </button>

                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => openViewRefundModal(r)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {refundOpen && selected ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b px-6 py-4">
              <div>
                <div className="text-2xl font-bold">Refund / Cancellation</div>
                <div className="text-sm text-gray-600 mt-1">
                  Booking: <b>{refundForm.booking_id}</b> • Payment: <b>{refundForm.payment_id}</b>
                </div>
              </div>

              <input type="hidden" name="user_id" value={refundForm.user_id || ""} />

              <button
                type="button"
                onClick={closeRefundModal}
                className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <form onSubmit={submitRefund} className="px-6 py-5">
              <div className="mb-5">
                <label className="text-sm font-semibold">
                  Cancellation Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  value={refundForm.cancel_type}
                  onChange={(e) => setField("cancel_type", e.target.value)}
                >
                  <option value="-Select-">-Select-</option>
                  <option value="Refund with Airline Penalty/ Void">Refund with Airline Penalty/ Void</option>
                  <option value="Partial Cancellation">Partial Cancellation</option>
                  <option value="Flight Not Operational">Flight Not Operational</option>
                  <option value="Change Itinerary / Reissue">Change Itinerary / Reissue</option>
                </select>
              </div>

              <div className="mb-5">
                <label className="text-sm font-semibold">
                  Refund Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  value={refundForm.refund_type}
                  onChange={(e) => {
                    const v = e.target.value;
                    setField("refund_type", v);

                    if (v !== "INSTANT_REFUND") setField("instant_refund_reason", "");
                  }}
                >
                  <option value="NORMAL_REFUND">Normal Refund</option>
                  <option value="INSTANT_REFUND">Instant Refund</option>
                </select>
              </div>

              {refundForm.refund_type === "INSTANT_REFUND" ? (
                <div className="mb-5">
                  <label className="text-sm font-semibold">
                    Why Instant Refund? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black min-h-[100px]"
                    placeholder="Enter reason for instant refund..."
                    value={refundForm.instant_refund_reason}
                    onChange={(e) => setField("instant_refund_reason", e.target.value)}
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    This is mandatory only for Instant Refund.
                  </div>
                </div>
              ) : null}

              <div className="border rounded-2xl p-4">
                <div className="text-lg font-bold mb-4">Amounts</div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold">Total Fare</label>
                    <input className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-gray-100" value={refundForm.totalfare} readOnly />
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Convenience Fee</label>
                    <input className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-gray-100" value={refundForm.convenience_fee} readOnly />
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Promo Applied</label>
                    <select className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-gray-100" value={refundForm.applied_promo_code} disabled>
                      <option value={0}>NO</option>
                      <option value={1}>YES</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Promo Code Amount</label>
                    <input className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-gray-100" value={refundForm.applied_promo_code_amount ?? ""} readOnly />
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Cashback Applied</label>
                    <select className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-gray-100" value={refundForm.applied_cashback} disabled>
                      <option value={0}>NO</option>
                      <option value={1}>YES</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Cashback Points</label>
                    <input className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-gray-100" value={refundForm.cashback_points ?? ""} readOnly />
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Wallet Applied</label>
                    <select className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-gray-100" value={refundForm.is_wallet_applied} disabled>
                      <option value={0}>NO</option>
                      <option value={1}>YES</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-semibold">Wallet Points</label>
                    <input className="mt-1 w-full border rounded-xl px-3 py-2 outline-none bg-gray-100" value={refundForm.wallet_points ?? ""} readOnly />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-sm font-semibold">Cancellation Amount</label>
                  <input
                    className="mt-1 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                    value={refundForm.cancellation_amount ?? ""}
                    onChange={(e) => setField("cancellation_amount", e.target.value)}
                    placeholder="Enter cancellation amount (if any)"
                  />
                  <div className="mt-2 text-sm text-gray-600">
                    Suggested refund after cancellation: <b>{money2(suggestedRefundAfterCancellation)}</b>
                  </div>
                </div>

                <div className="mt-5">
                  <label className="text-sm font-semibold">Refund Amount</label>
                  <input
                    className={`mt-1 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black ${
                      refundAmountError ? "border-red-500" : ""
                    } ${refundForm.full_payment ? "bg-gray-100" : ""}`}
                    value={refundForm.refund_amount}
                    onChange={(e) => {
                      setRefundAmountTouched(true);
                      setField("refund_amount", e.target.value);
                    }}
                    placeholder={`Max ${money2(maxRefundable)}`}
                    disabled={refundForm.full_payment}
                  />
                  {refundAmountError ? <div className="mt-2 text-sm text-red-600">{refundAmountError}</div> : null}

                  <div className="mt-2 text-sm text-gray-600">
                    Max refundable (full payment): <b>{money2(maxRefundable)}</b> (Total Fare - Convenience Fee - Promo Amount - Wallet Points if applied)
                  </div>

                  <label className="mt-3 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={refundForm.full_payment}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setField("full_payment", checked);

                        if (checked) {
                          setRefundAmountTouched(true);
                          setField("refund_amount", money2(maxRefundable));
                        } else {
                          setRefundAmountTouched(false);
                          setField("refund_amount", money2(suggestedRefundAfterCancellation));
                        }
                      }}
                    />
                    Full Payment Refund (sets refund amount to <b>{money2(maxRefundable)}</b>)
                  </label>
                </div>
              </div>

              <div className="mt-5">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={refundForm.cancel_insurance} onChange={(e) => setField("cancel_insurance", e.target.checked)} />
                  Cancel Insurance Booking
                </label>
              </div>

              <div className="mt-5">
                <label className="text-sm font-semibold">
                  Please enter remarks <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black min-h-[120px]"
                  placeholder="Enter remarks..."
                  value={refundForm.remarks}
                  onChange={(e) => setField("remarks", e.target.value)}
                />
              </div>

              <div className="mt-5">
                <label className="text-sm font-semibold">Attach Document</label>
                <input className="mt-2 block" type="file" onChange={(e) => setField("attachment", e.target.files?.[0] || null)} />
              </div>

              <div className="mt-6 flex items-center justify-end gap-3 border-t pt-4">
                <button type="button" onClick={closeRefundModal} className="rounded-xl border px-5 py-2 text-sm font-semibold hover:bg-gray-50" disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="rounded-xl bg-black px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  {submitting ? "Submitting..." : "Submit Refund"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {viewOpen ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div className="text-xl font-bold">Refund Details</div>
              <button type="button" onClick={closeViewRefundModal} className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50">
                Close
              </button>
            </div>

            <div className="px-6 py-5">
              {viewLoading ? (
                <div className="text-sm text-gray-600">Loading...</div>
              ) : !viewRow ? (
                <div className="text-sm text-gray-600">No refund submitted for this booking payment.</div>
              ) : (
                <div className="space-y-2 text-sm">
                  <div><b>Refund ID:</b> {viewRow.refund_id || "-"}</div>
                  <div><b>Booking ID:</b> {viewRow.booking_id}</div>
                  <div><b>Payment ID:</b> {viewRow.payment_id}</div>
                  <div><b>Refund Amount:</b> {viewRow.refund_amount}</div>
                  <div><b>Cancellation Amount:</b> {viewRow.cancellation_amount ?? "-"}</div>
                  <div><b>Cancel Type:</b> {viewRow.cancel_type}</div>
                  <div><b>Refund Type:</b> {viewRow.refund_type || "-"}</div>
                  {viewRow.refund_type === "INSTANT_REFUND" ? (
                    <div><b>Instant Reason:</b> {viewRow.instant_refund_reason || "-"}</div>
                  ) : null}
                  <div><b>Remarks:</b> {viewRow.remarks}</div>
                  <div><b>Full Payment:</b> {Number(viewRow.full_payment) === 1 ? "Yes" : "No"}</div>
                  <div><b>Cancel Insurance:</b> {Number(viewRow.cancel_insurance) === 1 ? "Yes" : "No"}</div>
                  <div><b>Created At:</b> {viewRow.created_at ? new Date(viewRow.created_at).toLocaleString() : "-"}</div>
                  {viewRow.attachment_path ? <div><b>Attachment:</b> {viewRow.attachment_path}</div> : null}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
